// Hooks for the stateful agent surfaces: the global prompts tab and the
// per-project runs tab. Mirrors inrange_agents/stateful_agents/routes.py.
// See docs/Y2026-Q3/design-stateful-agent-runs.md and
// docs/Y2026-Q3/design-rule-based-operator-prompts.md.
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { AsyncOperationEvent } from "./useAsyncOperation";
import type { ReferenceSummary } from "./useDashboard";
import type {
  ActionStatus,
  NextAction,
  TrackedDate,
} from "./useProjectAgentStatus";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";
import { runIsWorking } from "../../utils/runStatus";
import { agentLabelFrom } from "../../utils/runParticipants";

export { runIsWorking };

export interface DiffLine {
  kind: "context" | "added" | "removed" | "hunk";
  text: string;
}

export type RuleStatus = "active" | "draft" | "retired";

// One addressable rule. `improvement` is operator-only - it is a backlog of
// practice not yet adopted, so it never renders into the prompt.
export interface PromptRule {
  rule_id: string;
  category: string;
  title: string;
  guidance: string;
  why: string;
  limits: string;
  evidence: string;
  improvement: string;
  // Categorical values beside the text, keyed by the document's rule_attributes.
  attributes: Record<string, string>;
  status: RuleStatus;
  position: number;
}

export interface PromptAddendum {
  preamble: string;
  rules: PromptRule[];
  next_rule_seq: number;
}

export interface RuleChange {
  rule_id: string;
  title: string;
  kind: "added" | "removed" | "changed" | "status" | "reordered";
  fields_changed: string[];
  detail: string;
}

export interface PromptRevision {
  agent: string;
  revision: number;
  // The rendered text the agent receives, derived from `addendum` server-side.
  // Read-only here: editing happens on the rules.
  prompt_text: string;
  addendum: PromptAddendum;
  note: string;
  origin_kind:
    "seed" | "manual" | "restore" | "agent_proposal" | "seed_sync" | "reseed";
  origin_revision: number | null;
  created_at: string;
  created_by_email: string;
}

export interface PromptRevisionSummary {
  revision: number;
  note: string;
  origin_kind: string;
  origin_revision: number | null;
  created_at: string;
  created_by_email: string;
  char_count: number;
  rule_count: number;
}

/** A prompt document every agent reads (the team document), listed apart from the
 * roster because no agent runs it. */
export interface SharedPromptDocument {
  agent: string;
  label: string;
  description: string;
  // False while no agent's prompt includes it yet, so the page does not claim otherwise.
  read_by_agents: boolean;
}

export interface AgentList {
  agents: AgentPromptSummary[];
  documents: SharedPromptDocument[];
}

export interface AgentPromptSummary {
  agent: string;
  label: string;
  description: string;
  is_manager: boolean;
  delegates_to: string[];
  // Switched on per project on the project's settings tab, rather than working
  // every project whose scheduled runs are on.
  optional: boolean;
  // Keeps a per-project status row: a tab on each project it works, and a Portfolio
  // status page across them.
  keeps_status_row: boolean;
  // Its column's position on the deployment dashboard; null for no column.
  dashboard_column: number | null;
  // Its file carries the project's contract stages.
  keeps_contracts: boolean;
  active_revision: number;
  active_created_at: string;
  active_created_by_email: string;
  active_note: string;
  active_rule_count: number;
  active_char_count: number;
}

export interface RuleAttributeSpec {
  key: string;
  label: string;
  hint: string;
}

export interface AgentPromptDetail {
  agent: string;
  label: string;
  description: string;
  // Code-owned and read-only; shown so the addendum is not edited blind.
  scaffold_prompt: string;
  // The attributes this document's rules may carry, e.g. the team's member and email.
  rule_attributes: RuleAttributeSpec[];
  active: PromptRevision;
  revisions: PromptRevisionSummary[];
}

export interface StatefulRun {
  run_id: string;
  project_id: string;
  kind: "chat" | "agent_chat" | "scheduled" | "learning_review";
  agent?: string | null;
  // The real project id, or null for an agent-scoped run (never the sentinel).
  context_project_id?: string | null;
  // `null` means no ledger row, which must not render as zero.
  spend_usd?: number | null;
  config: string;
  title: string;
  status: "active" | "ended" | "interactive";
  started_by_email: string;
  /** The page a "Run now" was pressed on ("Legal"); null when nobody pressed
   * one. */
  started_from?: string | null;
  // Hidden from everyone but whoever started it. What the agent LEARNED from a
  // private conversation still applies for everyone; this hides the transcript.
  is_private?: boolean;
  // Whether this viewer started it, so the privacy toggle can be offered without
  // the FE knowing the server-side identity.
  is_owner?: boolean;
  // Everyone who has sent a message, oldest first. Agents are not people.
  participants?: string[];
  // When its owner hid it: in no list, read-only, nothing deleted.
  hidden_at?: string | null;
  // Which AGENTS worked in the run, first-acted first. The counterpart to
  // `participants`, which is the people in it.
  agents_in_run?: string[];
  // The sites this run is about, by name and sorted; empty means any site.
  projects?: string[];
  active_turn_operation_key: string | null;
  // A scheduled pass still being worked, on list payloads: it holds no turn pointer.
  pass_running?: boolean;
  has_draft: boolean;
  message_count: number;
  projects_worked: number;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  // When a scheduled pass finished; kept when the run is re-opened.
  pass_ended_at?: string | null;
}

// One agent's context window through a turn. `used` is where it finished, `peak` the
// largest it ever reached - which is NOT `used` when the turn compacted, because a
// compaction throws the peak away and that is the number saying how close it came.
export interface RunContextWindow {
  agent: string;
  used: number;
  window: number;
  peak: number;
  compactions: { before: number; after: number }[];
  // What filled the window where it finished; absent on turns before it was recorded.
  composition?: ContextComposition;
}

// What fills an agent's context window: approximate tokens by category (the
// server's `ContextCategory`), and within a tool-result category by tool. The
// categories add up to the window's `used`.
export interface ContextComposition {
  categories: Record<string, number>;
  tools?: Record<string, Record<string, number>>;
}

// One kind of thing a refresh brought in, counted in full and named up to a cap.
export interface RunSourceRefreshArrival {
  kind: "email" | "attachment" | "file";
  change: "new" | "updated";
  heading: string;
  one: string;
  many: string;
  count: number;
  uploads: { upload_id: string; name: string }[];
}

export interface RunSourceRefresh {
  error?: string | null;
  sources?: {
    label: string;
    status: "refreshed" | "partial" | "still_running" | "failed" | "skipped";
    detail: string;
    kind?: "gmail" | "drive" | "slack";
    // A Slack source's messages first seen during the refresh.
    new_messages?: number;
  }[];
  // Absent on refreshes recorded before they were grouped: only the count is known.
  arrivals?: RunSourceRefreshArrival[];
  new_upload_count?: number;
  // How many of `sources` are project Drives refreshed as the turn worked, at the end.
  during_turn?: number;
}

export interface RunTurnFlags {
  // "prose": the agent was asked to call conclude_turn and ended in prose anyway, which
  // the run page flags. "not_asked": it ended in prose without being asked (a wound-down
  // or stopped turn). "not_offered": the turn had no conclude_turn to call.
  concluded_via?: "tool" | "prose" | "not_asked" | "none" | "not_offered";
  turn_kind?: string;
  compactions?: number;
  // Per agent, where its window finished and what each compaction discarded.
  context?: RunContextWindow[];
  // An operator-ROLE message nobody sent: a scheduled pass's per-project brief.
  automated?: boolean;
  /** A review's first message: the comments it carries, shown in place of the text. */
  review_comments?: string[];
  incomplete_reason?: string;
  // reason: "timeout" | "released" | "error".
  extraction_gate_degraded?: { reason: string; pending?: string[] };
  // The Gmail + Drive refresh this turn waited for before it read anything.
  source_refresh?: RunSourceRefresh;
  // The turn's emails and withdrawals did not reach the project's email record.
  emails_not_recorded?: boolean;
  // The check for which emails went out failed at the start of the turn.
  emails_not_checked?: boolean;
  tool_failures?: {
    tool: string;
    upload_id: string | null;
    reason: string;
    detail: string;
  }[];
  // Rules this turn changed outright, on a re-opened pass.
  rules_applied?: string[];
  rules_failed?: string[];
  // A scheduled run's closing message listing what waited on the operator, written
  // before prompt changes stopped waiting on anyone. Never shown.
  prompt_changes?: unknown[];
  needs_you?: unknown[];
  // The operator's images this turn could not open. Mirrors the BE's
  // UNREADABLE_IMAGES_FLAG - must agree.
  unreadable_images?: string[];
  // On a project's write-up: the automated reviewer's check at the end of its pass.
  automated_review?: RunAutomatedReview;
  // An answer given on a question's card. Mirrors the BE's ANSWERED_QUESTION_FLAG.
  answered_question?: AnsweredQuestionFlag;
}

export interface AnsweredQuestionFlag {
  question_id: string;
  agent: string;
  question: string;
  chosen: { label: string; description: string }[];
  other: string;
}

export interface RunAutomatedReview {
  findings: {
    lane: "new_work" | "claims" | "gaps";
    kind: "wrong" | "unsupported" | "omission";
    owner: string;
    ref: string;
    item_id: string;
    claim: string;
    problem: string;
    evidence: string;
    severity: "high" | "medium";
    confidence: number;
    // Absent on reviews from before findings had a scope or a priority.
    scope?: "project" | "client";
    verdict: "" | "confirmed" | "refuted" | "unsure";
    verdict_reason: string;
    verdict_quote: string;
    priority?: "" | "material" | "low";
    priority_reason?: string;
    filed: boolean;
    not_filed_reason: string;
  }[];
  cost_usd: number;
  conversation_id: string | null;
  // Per lane: what it covered and what it cost; absent when it did not run.
  lanes: {
    new_work?: { changed: boolean; cost_usd?: number };
    claims?: { claims: number; critical: number; uncited: number; cost_usd?: number };
    gaps?: {
      area: string;
      cost_usd?: number;
      // Set when its last visit found nothing material: it read only what is new since.
      since?: string | null;
      skipped?: string;
    };
  };
  skipped: string;
}

export interface RunMessage {
  message_id: string;
  run_id: string;
  seq: number;
  role: "operator" | "agent";
  content: string;
  sender_email: string;
  agent: string | null;
  // Every agent that took part; `agent` is only who produced the answer.
  participants?: string[];
  prompt_revision: number | null;
  model_id: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  has_ai_trace: boolean;
  turn_flags: RunTurnFlags;
  // Send-ready messages the turn concluded with, shown beside its reply.
  drafts: RunDraft[];
  // Set on a scheduled pass's brief and report: the project it is about.
  project_id?: string | null;
  // What the operator attached, each with a URL to show it.
  images: RunMessageImage[];
  // The documents the operator attached: each a temporary extraction of the conversation.
  documents?: RunMessageDocument[];
  created_at: string;
}

export interface RunMessageDocument {
  /** Absent until the turn files it, and on one it could not open. */
  upload_id?: string;
  name: string;
  content_type: string;
}

export interface RunMessageImage {
  name: string;
  // Null until a browser-viewable copy exists (a HEIC before the agent has read it).
  url: string | null;
}

export interface RunDraft {
  to: string;
  subject: string;
  body: string;
  // When to send it, in the agent's words; "" when it gave none.
  trigger: string;
  // Absent on drafts written before emails carried them.
  why?: string;
  draft_id?: string;
  author?: string;
  merges?: string[];
  depends_on?: string[];
  /** The upload id of the email thread it replies on. */
  thread?: string;
}

// One email a scheduled pass left for a project (team_reports.email_view).
export interface RunEmail {
  draft_id: string;
  author: string;
  author_label: string;
  to: string;
  subject: string;
  body: string;
  trigger: string;
  why: string;
  depends_on: string[];
  merges: string[];
  /** The upload id of the email thread it replies on; "" for a new conversation. */
  thread: string;
  discharges: string[];
  previous_draft_id: string | null;
  first_drafted_at: string | null;
  status: "current" | "superseded" | "sent";
  sent_at: string | null;
  /** Written on an earlier run, shown on this project's latest one. */
  carried: boolean;
  /** Still unsent, but a later run worked the project and shows it there. */
  shown_later: boolean;
  /** The withheld rewrite, placed in an answer on this run, that kept this earlier email standing. */
  kept_by?: string | null;
}

// A specialist's one summary on a project (team_reports.merge_specialist_reports).
// Its emails are placed by draft id.
export interface SpecialistReport {
  agent: string;
  label: string;
  reply: string;
  concluded: boolean;
  draft_ids: string[];
}

// A specialist that did not work the project on this run but has emails waiting
// there: the answer that wrote them (team_reports._earlier_answers).
export interface EarlierAnswer extends SpecialistReport {
  run_id: string;
  created_at: string;
  /** What the Manager held back on the run that wrote it, with why. */
  withheld: Record<string, string>;
  emails: RunEmail[];
}

// One project a scheduled pass worked (team_reports.project_views).
export interface RunProjectView {
  project_id: string;
  project_name: string;
  /** The latest scheduled run that worked this project. */
  latest_run_id: string | null;
  brief_message_id: string | null;
  report_message_id: string | null;
  coordination: string;
  specialists: SpecialistReport[];
  earlier: EarlierAnswer[];
  withheld: Record<string, string>;
  // Set when the pass could not load or save this project's emails.
  email_problems: string[];
  emails: RunEmail[];
  counts: { emails: number; questions: number };
  /** What the run changed on this project, by reference (design 7.4); null on a run
   * that changed nothing here. */
  changes: RunChanges | null;
  /** Written to be read by reference; false for a run from before, read as prose. */
  by_reference: boolean;
}

export interface RunItemChange {
  agent: string;
  label: string;
  kind: "action" | "date";
  /** "create" | "update" | "log" | "close" */
  verb: string;
  /** Every reason the run gave for it, once per item however often it changed. */
  reasons: string[];
  item: NextAction & TrackedDate;
  status: ActionStatus | null;
}

/** An agent feedback question the run asked, or closed (run_changes.py). */
export interface RunQuestionChange {
  agent: string;
  label: string;
  verb: "ask" | "close";
  question_id: string;
  /** The page anchor it is answered at: the question, or an action's date move's action. */
  anchor: string;
  kind: "clarification" | "behavior" | "platform" | "date_move";
  question: string;
  status: string;
  closed_note: string | null;
}

export interface RunChanges {
  items: RunItemChange[];
  questions: RunQuestionChange[];
  records: {
    agent: string;
    label: string;
    sections: string[];
    reasons: string[];
  }[];
  attributes: {
    key: string;
    label: string;
    value: string;
    reason: string;
    agent: string;
  }[];
  summary: ReferenceSummary | null;
  /** ``{id: {agent, title}}`` for what the summary references. */
  titles: Record<string, { agent: string; title: string }>;
  /** ``{email: name}`` */
  team: Record<string, string>;
}

export interface StateChangeProposal {
  agent: string;
  label: string;
  sections_changed: string[];
  base_version: number | null;
  diff: DiffLine[];
  // The only account of a row-only change, when `diff` is empty.
  row_fields_changed: string[];
}

export interface InstructionChangeProposal {
  proposal_id: string;
  agent: string;
  label: string;
  action: "add" | "update" | "retire";
  rule_id: string | null;
  rule_title: string;
  base_revision: number;
  rationale: string;
  fields_changed: string[];
  diff: DiffLine[];
  // Set when the targeted rule moved while the run was open (still applies, rebased).
  rebased_onto_revision: number | null;
  // Non-empty when the rule was deleted, so the change cannot apply.
  unavailable_reason: string;
}

export interface CompletionProposal {
  state_changes: StateChangeProposal[];
  instruction_changes: InstructionChangeProposal[];
}

// The live turn per the operation row, null when none (incl. a self-healed dead
// worker): the honest liveness signal, unlike the run's pointer.
// How full each agent's context window is, by agent label: tokens used against the
// model's whole window.
export type ContextUsage = Record<
  string,
  { used: number; budget: number; composition?: ContextComposition }
>;

export interface LiveTurn {
  operation_key: string;
  status: "pending" | "running";
  cancel_requested: boolean;
  events: AsyncOperationEvent[];
  context?: ContextUsage;
  // Present while the turn prepares: syncing its sources, then waiting for documents.
  gate?: ExtractionGate | null;
}

export interface ExtractionGate {
  phase: "sync" | "documents";
  deadline_at: string | null;
  uploads: { upload_id: string; name: string; status: string }[];
}

// What a scheduled pass is doing now: it holds no turn pointer, so its current
// turn (one agent on one site) streams through a feed of its own.
export interface PassFeed {
  // "preparing": a hand-started pass syncing Gmail, Drive and Slack before its first
  // site; "" (or absent, from an older worker) for a site's turn.
  stage?: "" | "preparing";
  agent: string;
  project_id: string;
  project_name: string | null;
  // This project's place in the pass, 1-based, and how many the pass works. 0 from
  // a worker that predates them.
  site?: number;
  sites?: number;
  events: AsyncOperationEvent[];
  context?: ContextUsage;
}

export interface DraftStatus {
  status: RunEmail["status"];
  sent_at: string | null;
}

/** A message sent while a turn ran, posted when it ends; removable until then. */
export interface QueuedRunMessage {
  message_id: string;
  run_id: string;
  content: string;
  sender_email: string;
  images: RunMessageImage[];
  documents?: RunMessageDocument[];
  turn_flags?: RunTurnFlags;
  created_at: string;
}

export interface RunDetail {
  run: StatefulRun;
  messages: RunMessage[];
  // Optional: a cached detail held from before this field shipped has none.
  queued_messages?: QueuedRunMessage[];
  // Whether each email on the messages has since gone out or been replaced.
  draft_status: Record<string, DraftStatus>;
  turn: LiveTurn | null;
  // A scheduled pass holds no turn pointer, so ``turn`` alone reads it as idle.
  pass_running?: boolean;
  // One per lane a pass is working right now; empty between turns.
  pass_feeds?: PassFeed[];
  proposal: CompletionProposal | null;
  // Files the run's agent generated. Full reads only, like the proposal.
  // Optional: a cached detail held from before this field shipped has none.
  artifacts?: RunArtifact[];
  // The Google Drive documents this run created or revised; a later run that
  // revises one lists it on its own page.
  drive_documents?: RunDriveDocument[];
  // Set by the server, not inferred, so the merge matches what was returned.
  incremental?: boolean;
  // Each project a scheduled pass worked. Full reads only; empty on a run
  // recorded before messages carried their project.
  projects?: RunProjectView[];
}

export interface RunDriveDocument {
  document_id: string;
  filename: string;
  doc_format: string;
  web_view_link: string | null;
  created_by_agent: string;
  updated_at: string;
}

export interface RunArtifact {
  artifact_id: string;
  run_id: string;
  filename: string;
  content_type: string;
  size_bytes: number | null;
  // Set when the file was also saved into a project's files.
  project_id: string | null;
  upload_id: string | null;
  created_at: string;
}

// A file uploaded to the sender's holding area, ready to go with a message.
export interface HeldFile {
  s3_key: string;
  name: string;
}


export const useStatefulAgents = () => {
  const http = useAgentsHttp();
  return useQuery<AgentList>([QK.statefulAgents], () =>
    http<AgentList>("/stateful-agents/list", {}),
  );
};

/** The roster, and an agent key as its roster label. */
export const useAgentLabel = () => {
  const roster = useStatefulAgents().data?.agents ?? [];
  return { roster, agentLabel: agentLabelFrom(roster) };
};

export const useStatefulAgentPrompt = (agent: string | null) => {
  const http = useAgentsHttp();
  return useQuery<AgentPromptDetail>(
    [QK.statefulPrompt, agent],
    () => http<AgentPromptDetail>("/stateful-agents/prompt/get", { agent }),
    { enabled: !!agent },
  );
};

// Every prompt write appends a revision, so both the detail and the roster
// (which shows each agent's active revision + size) go stale together.
const invalidatePrompt = (
  qc: ReturnType<typeof useQueryClient>,
  agent: string,
) => {
  void qc.invalidateQueries([QK.statefulPrompt, agent]);
  void qc.invalidateQueries([QK.statefulAgents]);
};

// The whole-set replace. The per-rule hooks below are the everyday path.
export const useSavePrompt = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    PromptRevision,
    Error,
    {
      base_revision: number;
      addendum: PromptAddendum;
      note: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<PromptRevision>("/stateful-agents/prompt/save", { agent, ...body }),
    {
      onSuccess: () => invalidatePrompt(qc, agent),
    },
  );
};

// `rule_id` omitted adds a rule; present updates one. Fields left out of `rule`
// keep their stored text, so a partial edit cannot blank what it did not touch.
export const useSaveRule = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { revision: PromptRevision; rule_id: string },
    Error,
    {
      base_revision: number;
      rule_id?: string;
      rule: Partial<Omit<PromptRule, "rule_id" | "position">>;
      note?: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<{ revision: PromptRevision; rule_id: string }>(
        "/stateful-agents/prompt/rule/save",
        { agent, ...body },
      ),
    { onSuccess: () => invalidatePrompt(qc, agent) },
  );
};

export const useDeleteRule = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    PromptRevision,
    Error,
    {
      base_revision: number;
      rule_id: string;
      note?: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<PromptRevision>("/stateful-agents/prompt/rule/delete", {
        agent,
        ...body,
      }),
    {
      onSuccess: () => invalidatePrompt(qc, agent),
    },
  );
};

export const useReorderRules = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    PromptRevision,
    Error,
    {
      base_revision: number;
      rule_ids: string[];
      note?: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<PromptRevision>("/stateful-agents/prompt/reorder", {
        agent,
        ...body,
      }),
    {
      onSuccess: () => invalidatePrompt(qc, agent),
    },
  );
};

export const useSavePreamble = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    PromptRevision,
    Error,
    {
      base_revision: number;
      preamble: string;
      note?: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<PromptRevision>("/stateful-agents/prompt/preamble/save", {
        agent,
        ...body,
      }),
    {
      onSuccess: () => invalidatePrompt(qc, agent),
    },
  );
};

export const useRestorePrompt = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    PromptRevision,
    Error,
    {
      revision: number;
      base_revision: number;
      note: string;
      actor_email: string;
    }
  >(
    (body) =>
      http<PromptRevision>("/stateful-agents/prompt/restore", {
        agent,
        ...body,
      }),
    {
      onSuccess: () => invalidatePrompt(qc, agent),
      // A stale base: refetch so the tab shows what moved, and the next Restore is
      // sent against the current revision instead of failing the same way again.
      onError: (error) => {
        if ((error as { status?: number }).status === 409)
          invalidatePrompt(qc, agent);
      },
    },
  );
};

export const usePromptDiff = (
  agent: string | null,
  fromRevision: number | null,
  toRevision: number | null,
) => {
  const http = useAgentsHttp();
  return useQuery<{
    lines: DiffLine[];
    rule_changes: RuleChange[];
    preamble_changed: boolean;
  }>(
    [QK.statefulPrompt, agent, "diff", fromRevision, toRevision],
    () =>
      http<{
        lines: DiffLine[];
        rule_changes: RuleChange[];
        preamble_changed: boolean;
      }>("/stateful-agents/prompt/diff", {
        agent,
        from_revision: fromRevision,
        to_revision: toRevision,
      }),
    { enabled: !!agent && toRevision !== null },
  );
};

export const useStatefulRuns = (
  projectId: string | undefined,
  range?: { from: string; to: string },
  // Filtered SERVER-side: splitting a capped list client-side drops rows.
  kind?: "scheduled" | "conversations",
  // Conversation types (review, other); none means every type.
  types: string[] = [],
) => {
  const http = useAgentsHttp();
  return useQuery<{
    runs: StatefulRun[];
    active_run_id: string | null;
    project_name?: string;
  }>(
    [QK.statefulRuns, projectId, range?.from, range?.to, kind, types],
    () =>
      http<{
        runs: StatefulRun[];
        active_run_id: string | null;
        project_name?: string;
      }>("/stateful-runs/list", {
        project_id: projectId,
        ...(range ? { from: range.from, to: range.to } : {}),
        // The server bounds days in this zone, so they are the operator's days.
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(kind ? { kind } : {}),
        ...(types.length ? { types } : {}),
      }),
    {
      enabled: !!projectId,
      // Polled only while a row is working, so its "Working…" clears when it ends.
      refetchInterval: (data) =>
        data?.runs.some(runIsWorking) ? PASS_POLL_MS : false,
    },
  );
};

export interface LastScheduledRun {
  run_id: string;
  created_at: string;
  /** Empty for the daily pass, which nobody started. */
  started_by_email: string;
  running: boolean;
}

/** When the team last ran over this site (last_scheduled.py): the daily pass or a
 * rerun, never a comment review or a conversation. Keyed under the runs so anything
 * that refreshes the project's runs refreshes it too. */
export const useLastScheduledRun = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<{ run: LastScheduledRun | null }>(
    [QK.statefulRuns, projectId, "last-scheduled"],
    () =>
      http<{ run: LastScheduledRun | null }>("/stateful-runs/last-scheduled", {
        project_id: projectId,
      }),
    {
      enabled: !!projectId,
      refetchInterval: (data) => (data?.run?.running ? PASS_POLL_MS : false),
    },
  );
};

// Frequent but cheap (cursor, no proposal rebuild): a turn can run for hours.
const RUN_POLL_MS = 2000;
const PASS_POLL_MS = 15_000;

const mergeMessages = (
  held: RunMessage[],
  incoming: RunMessage[],
): RunMessage[] => {
  if (incoming.length === 0) return held;
  // Key on message_id and keep the fresh copy: it carries has_ai_trace, set
  // just AFTER the message row is appended.
  const byId = new Map(held.map((m) => [m.message_id, m]));
  for (const m of incoming) byId.set(m.message_id, m);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
};

// One poll for everything the surface needs (principle 31). In flight it sends
// a `since_seq` cursor; when the turn goes terminal one FULL read follows, since
// the proposal and `has_ai_trace` change at that boundary.
export const useStatefulRun = (runId: string | null) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  // Not in the query key: that would mint a cache entry per tick.
  const cursor = useRef<{ runId: string | null; seq: number | null }>({
    runId: null,
    seq: null,
  });

  return useQuery<RunDetail>(
    [QK.statefulRun, runId],
    async () => {
      const held = qc.getQueryData<RunDetail>([QK.statefulRun, runId]);
      // Incremental only for THIS run's cursor with a turn (or a pass) live at the last tick.
      const resumable =
        cursor.current.runId === runId &&
        (held?.turn || held?.pass_feeds?.length) &&
        cursor.current.seq !== null;
      const body = resumable
        ? { run_id: runId, since_seq: cursor.current.seq }
        : { run_id: runId };
      let page = await http<RunDetail>("/stateful-runs/get", body);
      // A project a live pass just concluded: its view comes with a full read only,
      // and without one the reply renders as the raw transcript until the pass ends.
      const reported = new Set(
        (held?.projects ?? []).map((p) => p.report_message_id),
      );
      const newReport =
        page.incremental &&
        page.pass_running &&
        page.messages.some(
          (m) =>
            m.role === "agent" && m.project_id && !reported.has(m.message_id),
        );
      if (
        page.incremental &&
        ((page.turn === null && !page.pass_feeds?.length) || newReport)
      ) {
        // The turn (or the pass) landed between ticks, or a pass finished a project:
        // take the full read now.
        page = await http<RunDetail>("/stateful-runs/get", { run_id: runId });
        // The turn's spend lands as it ends; nothing else invalidates this read.
        void qc.invalidateQueries([QK.costLineItems]);
      }
      const messages = page.incremental
        ? mergeMessages(held?.messages ?? [], page.messages)
        : page.messages;
      cursor.current = {
        runId,
        seq: messages.length > 0 ? messages[messages.length - 1].seq : null,
      };
      // Incremental reads carry no proposal; the held copy is safe because the
      // turn-landed boundary above always takes a full read.
      const proposal = page.incremental
        ? (held?.proposal ?? page.proposal)
        : page.proposal;
      // Same reason as the proposal: an incremental read carries neither.
      const artifacts = page.incremental
        ? (held?.artifacts ?? page.artifacts)
        : page.artifacts;
      // ...and so does this, which is why it is held the same way. Dropped, the
      // Drive documents panel vanishes for the whole of a live turn - exactly when
      // the agent has just written one and the operator is looking for the link.
      const driveDocuments = page.incremental
        ? (held?.drive_documents ?? page.drive_documents)
        : page.drive_documents;
      // A full read only, like the artifacts: an incremental page carries none.
      const projects = page.incremental
        ? (held?.projects ?? page.projects)
        : page.projects;
      // An incremental read carries only its own messages' emails.
      const draftStatus = page.incremental
        ? { ...(held?.draft_status ?? {}), ...page.draft_status }
        : page.draft_status;
      return {
        ...page,
        messages,
        draft_status: draftStatus,
        proposal,
        artifacts,
        drive_documents: driveDocuments,
        projects,
      };
    },
    {
      enabled: !!runId,
      // Only while something runs. The claimed pointer also keeps polling, since
      // a DDB blip can null `turn` for a tick (the server releases a dead
      // pointer, so it cannot spin forever). A scheduled pass polls like a turn
      // while its feed is live, and slowly otherwise.
      refetchInterval: (data) =>
        data?.turn ||
        data?.run.active_turn_operation_key ||
        data?.queued_messages?.length ||
        data?.pass_feeds?.length
          ? RUN_POLL_MS
          : data?.pass_running
            ? PASS_POLL_MS
            : false,
    },
  );
};

interface LaunchResponse {
  run_id: string;
  /** Absent when the message was queued behind the running turn. */
  operation_key?: string;
  /** "pending", or "queued" behind the running turn. */
  status: string;
}

interface ArtifactDownload {
  url: string;
  filename: string;
  content_type: string;
}

// Minted on demand rather than on every run read, so a transcript poll never
// signs a URL per generated file.
export const useRunArtifactDownloadUrl = (runId: string) => {
  const http = useAgentsHttp();
  return useMutation<ArtifactDownload, Error, { artifact_id: string }>((body) =>
    http<ArtifactDownload>("/stateful-runs/artifact-download", {
      run_id: runId,
      ...body,
    }),
  );
};

// Removing a generated file takes the project's copy and the bytes with it, so the
// run's read is invalidated rather than patched: the panel must not keep showing a
// file that is gone.
export const useDeleteRunArtifact = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { deleted: boolean; filename: string },
    Error,
    { artifact_id: string }
  >(
    (body) =>
      http<{ deleted: boolean; filename: string }>(
        "/stateful-runs/artifact-delete",
        {
          run_id: runId,
          ...body,
        },
      ),
    {
      onSuccess: (_data, { artifact_id }) => {
        // Dropped from the held detail FIRST. The run read is incremental while a
        // turn is live, and an incremental page carries no artifacts - so an
        // invalidate alone re-serves the held list, and the file an operator has
        // just deleted stays on screen for the rest of the turn.
        qc.setQueryData<RunDetail | undefined>([QK.statefulRun, runId], (held) =>
          held
            ? {
                ...held,
                artifacts: (held.artifacts ?? []).filter(
                  (a) => a.artifact_id !== artifact_id,
                ),
              }
            : held,
        );
        void qc.invalidateQueries([QK.statefulRun, runId]);
      },
    },
  );
};

export const useRenameStatefulRun = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ run: StatefulRun }, Error, { title: string }>(
    (body) =>
      http<{ run: StatefulRun }>("/stateful-runs/rename", {
        run_id: runId,
        ...body,
      }),
    { onSuccess: (data) => applyRun(qc, runId, data.run) },
  );
};

export const useRegenerateStatefulRunName = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ run: StatefulRun }, Error, void>(
    () =>
      http<{ run: StatefulRun }>("/stateful-runs/regenerate-name", {
        run_id: runId,
      }),
    { onSuccess: (data) => applyRun(qc, runId, data.run) },
  );
};

export const useSetStatefulRunHidden = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ run: StatefulRun }, Error, { hidden: boolean }>(
    (body) =>
      http<{ run: StatefulRun }>("/stateful-runs/set-hidden", {
        run_id: runId,
        ...body,
      }),
    { onSuccess: (data) => applyRun(qc, runId, data.run) },
  );
};

export const useSetStatefulRunPrivacy = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ run: StatefulRun }, Error, { is_private: boolean }>(
    (body) =>
      http<{ run: StatefulRun }>("/stateful-runs/set-privacy", {
        run_id: runId,
        ...body,
      }),
    { onSuccess: (data) => applyRun(qc, runId, data.run) },
  );
};

// Stop a scheduled pass: no further site starts and the one in hand winds down at
// its next tool boundary. `status` is "cancelling", or "finished" when it had ended.
export const useStopPass = (runId: string) => {
  const http = useAgentsHttp();
  return useMutation<{ status: string; cancel_requested: boolean }, Error, void>(
    () =>
      http<{ status: string; cancel_requested: boolean }>(
        "/stateful-runs/stop-pass",
        { run_id: runId },
      ),
  );
};

// Each of these routes returns the run it just changed, so the held detail is
// updated from the response rather than waiting for a refetch: invalidation is
// fire-and-forget, so a rename that only invalidated would render the OLD title
// for a round trip and read as "it didn't take". The lists are keyed per project
// and per agent, so those do just refetch.
const applyRun = (
  qc: ReturnType<typeof useQueryClient>,
  runId: string,
  run: StatefulRun,
): void => {
  qc.setQueryData<RunDetail | undefined>([QK.statefulRun, runId], (held) =>
    held ? { ...held, run } : held,
  );
  void qc.invalidateQueries([QK.statefulRun, runId]);
  void qc.invalidateQueries([QK.statefulRuns]);
  // The specialists' own Conversations tabs (useAgentPortfolio.useAgentRuns).
  void qc.invalidateQueries([QK.agentRuns]);
  // Links elsewhere that name the run by its title.
  void qc.invalidateQueries([QK.runsDescribe]);
};

export const useStartStatefulRun = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    LaunchResponse,
    Error,
    {
      project_id: string;
      /** The agent slot the conversation runs in. */
      config: string;
      // Omitted with a draft ``action``: the server words the message from the action.
      content?: string;
      actor_email: string;
      is_private?: boolean;
      // Who ANSWERS. Omitted for the suite fronted by the Manager, which is the
      // default; a specialist name puts that specialist in the chair.
      agent?: string;
      /** Started from a next action's button (action_requests.py). */
      action?: {
        action_id: string;
        intent: "draft_email" | "draft_reminder" | "discuss";
      };
      // Omitted, not [], for "the whole team": the server reads absent as
      // unrestricted and an empty list as the Manager alone.
      specialists?: string[];
      images?: HeldFile[];
      documents?: HeldFile[];
    }
  >((body) => http<LaunchResponse>("/stateful-runs/start", body), {
    onSuccess: (_, { project_id }) =>
      void qc.invalidateQueries([QK.statefulRuns, project_id]),
  });
};

export const useStatefulRunTurn = (runId: string, projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    LaunchResponse,
    Error,
    { content: string; actor_email: string; images?: HeldFile[]; documents?: HeldFile[] }
  >(
    (body) =>
      http<LaunchResponse>("/stateful-runs/turn", { run_id: runId, ...body }),
    {
      onSuccess: () => {
        void qc.invalidateQueries([QK.statefulRun, runId]);
        void qc.invalidateQueries([QK.statefulRuns, projectId]);
      },
    },
  );
};

/** Takes back a message queued behind the running turn. */
export const useRemoveQueuedMessage = (runId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>(
    (messageId) =>
      http("/stateful-runs/queued-message/remove", {
        run_id: runId,
        message_id: messageId,
      }),
    { onSettled: () => void qc.invalidateQueries([QK.statefulRun, runId]) },
  );
};

// A conversation with an agent about ITSELF; the server supplies the sentinel.
/** ``agent`` is the DEFAULT; a call may name a different one.
 *
 * Bound per call rather than at hook creation because the composer reports who
 * should answer at submit time - reading it from a hook created earlier in the
 * same render would use the previous selection. */
export const useStartAgentChat = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    LaunchResponse,
    Error,
    {
      content: string;
      actor_email: string;
      is_private?: boolean;
      specialists?: string[];
      // Omitted for "every project on the platform".
      scope_project_ids?: string[];
      // Overrides the hook's default for this call.
      agent?: string;
      images?: HeldFile[];
      documents?: HeldFile[];
    }
  >(
    (body) =>
      http<LaunchResponse>("/stateful-runs/start-agent-chat", {
        agent,
        ...body,
      }),
    {
      onSuccess: () => {
        void qc.invalidateQueries([QK.statefulRuns]);
        void qc.invalidateQueries([QK.agentRuns]);
      },
    },
  );
};


// The behavior questions waiting on an operator, per agent, in one call (no N+1).
export interface AgentAttention {
  total: number;
  agents: Record<string, { questions: number }>;
}

export const useAgentAttention = (options: { enabled?: boolean } = {}) => {
  const http = useAgentsHttp();
  return useQuery<AgentAttention>(
    [QK.agentAttention],
    () => http<AgentAttention>("/stateful-agents/attention", {}),
    {
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
      enabled: options.enabled ?? true,
    },
  );
};

// Behavior questions waiting on an operator that are the viewer's or nobody's, per prompt
// page and in all: the Agents nav badge and the Learning agents badges.
export const useQuestionsWaiting = (options: { enabled?: boolean } = {}) => {
  const attention = useAgentAttention(options);
  const byAgent = Object.fromEntries(
    Object.entries(attention.data?.agents ?? {}).map(([agent, counts]) => [
      agent,
      counts.questions,
    ]),
  );
  const total = Object.values(byAgent).reduce((sum, n) => sum + n, 0);
  return { byAgent, total };
};

// One completed turn's progress trace, fetched on demand (`enabled` = the operator
// opened it) rather than on every run read. A turn without one resolves to null.
export const useStatefulTurnTrace = (
  runId: string | null,
  messageId: string | null,
  enabled: boolean,
) => {
  const http = useAgentsHttp();
  return useQuery<string | null>(
    [QK.statefulTurnTrace, runId, messageId],
    async () => {
      const resp = await http<{ trace: string | null }>(
        "/stateful-runs/turn-trace",
        {
          run_id: runId,
          message_id: messageId,
        },
      );
      return resp.trace;
    },
    {
      enabled: enabled && Boolean(runId && messageId),
      refetchOnWindowFocus: false,
      // A finished turn's trace never changes - hold it for the session.
      staleTime: Infinity,
    },
  );
};
